// Per-user favourites (#1). Stored server-side in D1, keyed on the Cloudflare
// Access identity, so the same login sees the same list on every device. Replaces
// the old per-device localStorage set. The public API ({ favourites, isFavourite,
// toggle }) is unchanged, so cards / detail / header don't need to change.
//
// Favourites are a logged-in-only feature (the heart only renders for the owner),
// so the query is gated on authentication; anonymous visitors get an empty set.

import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useIsAuthenticated } from './auth';

const QUERY_KEY = ['favourites'] as const;

// One-time import of the old per-device localStorage favourites into the
// server list, so existing favourites aren't lost on the migration.
const LEGACY_KEY = 'recipes:favourites';
const MIGRATED_KEY = 'recipes:favourites:migrated';

function readLegacy(): number[] {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((n): n is number => Number.isInteger(n)) : [];
  } catch {
    return [];
  }
}

export function useFavourites() {
  const isAuthenticated = useIsAuthenticated();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: api.listFavourites,
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  });

  const favourites = useMemo(() => new Set<number>(data ?? []), [data]);

  const mutation = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      on ? api.addFavourite(id) : api.removeFavourite(id),
    // Optimistic toggle so the heart reacts instantly.
    onMutate: async ({ id, on }) => {
      await qc.cancelQueries({ queryKey: QUERY_KEY });
      const prev = qc.getQueryData<number[]>(QUERY_KEY) ?? [];
      const next = on ? [...new Set([...prev, id])] : prev.filter((x) => x !== id);
      qc.setQueryData<number[]>(QUERY_KEY, next);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData<number[]>(QUERY_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  // Best-effort, run-once migration of legacy localStorage favourites.
  useEffect(() => {
    if (!isAuthenticated || data === undefined) return;
    let migrated = false;
    try {
      migrated = localStorage.getItem(MIGRATED_KEY) === '1';
    } catch {
      return;
    }
    if (migrated) return;
    try {
      localStorage.setItem(MIGRATED_KEY, '1');
    } catch {
      // ignore
    }
    const missing = readLegacy().filter((id) => !favourites.has(id));
    if (missing.length === 0) return;
    Promise.all(missing.map((id) => api.addFavourite(id).catch(() => undefined))).then(() =>
      qc.invalidateQueries({ queryKey: QUERY_KEY }),
    );
  }, [isAuthenticated, data, favourites, qc]);

  const isFavourite = (id: number) => favourites.has(id);
  const toggle = (id: number) => mutation.mutate({ id, on: !favourites.has(id) });

  return { favourites, isFavourite, toggle };
}
